import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Link, List, ListItem, ListItemText, Pagination,
  Stack, Tooltip, Typography,
} from '@mui/material';
import {
  EditNote as DraftIcon,
  Mic as MicIcon,
  ArrowForward as GoIcon,
  DeleteOutline as DiscardIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** Old drafts are flagged: an unfinished visit still holds the patient's recording. */
const STALE_HOURS = 24;

/** Below this many hours left, the countdown is shown in red rather than amber. */
const URGENT_HOURS = 4;

/** Rows per page. The list is ordered oldest-first, so page 1 is always the most at
 *  risk of losing its recording — the reason to look at this card at all. */
const PAGE_SIZE = 5;

function age(hours) {
  if (hours < 1) return 'just now';
  if (hours < 24) return `${Math.round(hours)}h ago`;
  const d = Math.round(hours / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

/** How long until this visit's recording is deleted. Null when it holds none. */
function expiry(hours) {
  if (hours === null || hours === undefined) return null;
  if (hours <= 0) return 'Recording expired';
  if (hours < 1) return 'Recording deleted within the hour';
  if (hours < 24) return `Recording deleted in ${Math.round(hours)}h`;
  const d = Math.floor(hours / 24);
  return `Recording deleted in ${d} day${d === 1 ? '' : 's'}`;
}

/**
 * Visits this clinician started but never closed.
 *
 * The record already survived being walked away from — a draft sits in review
 * indefinitely with its transcript intact. What was missing was any way back to it: a
 * draft was reachable only from that patient's own record, so you had to remember which
 * patients you had left open.
 *
 * Renders nothing when there is nothing outstanding, so it stays out of the way on a
 * normal day.
 */
export default function UnfinishedVisits() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);   // the visit being discarded
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);

  const pageCount = Math.max(1, Math.ceil(sessions.length / PAGE_SIZE));
  // Discarding the last row on the last page would otherwise leave the card blank on a
  // page that no longer exists.
  const current = Math.min(page, pageCount);
  const pageRows = sessions.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/clinical/sessions/unfinished`);
      setSessions(data?.sessions || []);
      setRetention(data?.retention_label || null);
    } catch {
      setSessions([]);          // never let this break the dashboard around it
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || sessions.length === 0) return null;

  // Only visits that still hold audio, because the banner's whole point is "close them
  // while the recording is still there". Counting the rest made it warn about losing
  // something that had already gone, or never existed.
  const stale = sessions.filter((s) => s.age_hours >= STALE_HOURS && s.has_audio).length;
  // Anything whose recording goes today is what this panel is really for.
  const expiring = sessions.filter(
    (s) => s.audio_expires_in_hours !== null
      && s.audio_expires_in_hours !== undefined
      && s.audio_expires_in_hours <= 14,
  ).length;

  /**
   * Throw one away without opening it. This is where abandoned visits are actually
   * found, so it is where it is most useful to be able to clear one — a recording
   * started on the wrong patient does not deserve a round trip through the recorder.
   */
  const discard = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(
        `${API_URL}/api/v1/clinical/session/${confirm.session_id}`);
      toast.success(data?.message || 'Visit discarded');
      setSessions((prev) => prev.filter((x) => x.session_id !== confirm.session_id));
      setConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not discard that visit');
    } finally {
      setBusy(false);
    }
  };

  const open = (s) => {
    if (s.resume === 'review') navigate(`/review/${s.session_id}`);
    else navigate(`/scribe/${s.patient_id}`);
  };

  return (
    <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'warning.main' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <DraftIcon color="warning" />
          <Typography variant="h6" fontWeight={700}>
            Unfinished visits ({sessions.length})
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Started but not finalized, and still holding their recording. The note and
          transcript are waiting exactly as you left them.{' '}
          {/* When the audio expires a visit leaves this card but not the product — say
              where it went, or it looks like it was lost. */}
          <Link component="button" type="button" underline="hover"
            onClick={() => navigate('/drafts')} sx={{ verticalAlign: 'baseline' }}>
            See every draft
          </Link>, including those whose recording has expired.
        </Typography>

        {expiring > 0 ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {expiring === 1
              ? 'One of these loses its recording today.'
              : `${expiring} of these lose their recordings today.`}{' '}
            Finish {expiring === 1 ? 'the note' : 'them'} while you can still play the
            consultation back — afterwards the transcript and draft remain, but the audio
            does not.
          </Alert>
        ) : stale > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {stale === 1 ? 'One of these is' : `${stale} of these are`} more than a day
            old. Recordings are kept for {retention || 'a limited period'} and then
            deleted, finalized or not, so please close {stale === 1 ? 'it' : 'them'} while
            the audio is still there.
          </Alert>
        )}

        <Divider />
        <List dense disablePadding>
          {pageRows.map((s) => {
            const isStale = s.age_hours >= STALE_HOURS;
            const left = s.audio_expires_in_hours;
            const countdown = expiry(left);
            const urgent = countdown !== null && left <= URGENT_HOURS;
            return (
              <ListItem
                key={s.session_id}
                divider
                secondaryAction={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title="Discard this visit">
                      <IconButton size="small" onClick={() => setConfirm(s)}>
                        <DiscardIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Button
                      size="small"
                      variant={isStale ? 'contained' : 'outlined'}
                      color={isStale ? 'warning' : 'primary'}
                      endIcon={<GoIcon />}
                      onClick={() => open(s)}
                    >
                      {s.resume === 'review' ? 'Review' : 'Resume'}
                    </Button>
                  </Stack>
                }
                sx={{ px: 0 }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Typography fontWeight={600}>{s.patient_name}</Typography>
                      <Chip
                        size="small"
                        icon={s.resume === 'review' ? <DraftIcon /> : <MicIcon />}
                        // `resume` says where the button sends you, not whether audio
                        // exists — labelling every non-drafted visit "Recording" claimed
                        // one even when has_audio was false.
                        label={s.resume === 'review'
                          ? 'Note drafted'
                          : (s.has_audio ? 'Recording' : 'No recording')}
                        color={s.resume === 'review' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                      {isStale && <Chip size="small" color="warning" label="Older than a day" />}
                      {countdown && (
                        <Chip
                          size="small"
                          color={urgent ? 'error' : 'warning'}
                          variant={urgent ? 'filled' : 'outlined'}
                          label={countdown}
                        />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Box component="span">
                      {s.reason ? `${s.reason} · ` : ''}started {age(s.age_hours)}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>

        {pageCount > 1 && (
          <Stack direction="row" justifyContent="space-between" alignItems="center"
                 sx={{ pt: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Showing {(current - 1) * PAGE_SIZE + 1}–
              {Math.min(current * PAGE_SIZE, sessions.length)} of {sessions.length},
              oldest first
            </Typography>
            <Pagination
              size="small"
              count={pageCount}
              page={current}
              onChange={(_, p) => setPage(p)}
            />
          </Stack>
        )}
      </CardContent>

      <Dialog open={Boolean(confirm)} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>Discard {confirm?.patient_name}'s visit?</DialogTitle>
        <DialogContent>
          <Typography>
            The recording will be <strong>permanently deleted</strong>, along with any
            note drafted from it. This cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Any vitals recorded during the visit stay on the patient's record.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Keep it</Button>
          <Button color="error" variant="contained" onClick={discard} disabled={busy}>
            {busy ? 'Discarding…' : 'Discard visit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
